/**
 * Window Manager Utilities
 * Functions to manage split-screen window layouts
 */

/**
 * Opens a new browser window positioned on the LEFT-hand side of the screen
 * @param {string} url - The URL to open
 * @param {string} name - Window name/target
 * @param {number} widthRatio - Fraction of screen width (0..1) for the new window
 * @returns {Window|null} - The opened window reference or null
 */
export function openLeftSidebar(url, name = 'leftSidebar', widthRatio = 0.5) {
    try {
        const availWidth = window.screen.availWidth || window.screen.width;
        const availHeight = window.screen.availHeight || window.screen.height;
        const width = Math.max(200, Math.floor(availWidth * widthRatio));
        const height = Math.max(200, Math.floor(availHeight));
        const left = 0; // Position on the LEFT side
        const top = 0;

        // Features string for window positioning
        const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`;

        const win = window.open(url, name, features);

        if (win) {
            // Try to focus the opened window
            try {
                win.focus();
            } catch (e) {
                console.warn('Could not focus window:', e);
            }
            return win;
        } else {
            alert('Unable to open window. Your browser may be blocking popups for this site. Please allow popups and try again.');
            return null;
        }
    } catch (err) {
        console.error('openLeftSidebar error:', err);
        alert('An unexpected error occurred while trying to open the window. See console for details.');
        return null;
    }
}

/**
 * Opens a new browser window positioned on the right-hand side of the screen
 * @param {string} url - The URL to open
 * @param {string} name - Window name/target
 * @param {number} widthRatio - Fraction of screen width (0..1) for the new window
 * @returns {Window|null} - The opened window reference or null
 */
export function openRightSidebar(url, name = 'rightSidebar', widthRatio = 0.8) {
    try {
        const availWidth = window.screen.availWidth || window.screen.width;
        const availHeight = window.screen.availHeight || window.screen.height;
        const width = Math.max(200, Math.floor(availWidth * widthRatio));
        const height = Math.max(200, Math.floor(availHeight));
        const left = Math.max(0, Math.floor(availWidth - width));
        const top = 0;

        // Features string for window positioning
        const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`;

        const win = window.open(url, name, features);

        if (win) {
            // Try to focus the opened window
            try {
                win.focus();
            } catch (e) {
                console.warn('Could not focus window:', e);
            }
            return win;
        } else {
            alert('Unable to open window. Your browser may be blocking popups for this site. Please allow popups and try again.');
            return null;
        }
    } catch (err) {
        console.error('openRightSidebar error:', err);
        alert('An unexpected error occurred while trying to open the window. See console for details.');
        return null;
    }
}

/**
 * Reframes the current window to the RIGHT side of the screen
 * @param {number} widthRatio - Fraction of screen width (0..1) for the current window
 * @returns {boolean} - True if successful, false otherwise
 */
export function reframeRightWindow(widthRatio = 0.5) {
    try {
        const availWidth = window.screen.availWidth || window.screen.width;
        const availHeight = window.screen.availHeight || window.screen.height;
        const newWidth = Math.max(200, Math.floor(availWidth * widthRatio));
        const newHeight = Math.max(200, Math.floor(availHeight));
        const newLeft = Math.max(0, Math.floor(availWidth - newWidth)); // Position on RIGHT
        const newTop = 0;

        // Try to resize/move current window
        let moved = false;
        try {
            window.resizeTo(newWidth, newHeight);
            window.moveTo(newLeft, newTop);
            moved = true;
            return true;
        } catch (e) {
            console.warn('resize/move not allowed for current window:', e);
        }

        // If not allowed, open a new window positioned on the right as a fallback
        if (!moved) {
            const features = `width=${newWidth},height=${newHeight},left=${newLeft},top=${newTop},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`;
            const win = window.open(window.location.href, 'rightReframe', features);
            if (!win) {
                alert('Unable to reframe the window. Your browser may be blocking popups or does not allow moving/resizing of existing windows.');
                return false;
            }
            return true;
        }
    } catch (err) {
        console.error('reframeRightWindow error:', err);
        alert('An unexpected error occurred while trying to reframe the window. See console for details.');
        return false;
    }
}

/**
 * Reframes the current window to the left side of the screen
 * @param {number} widthRatio - Fraction of screen width (0..1) for the current window
 * @returns {boolean} - True if successful, false otherwise
 */
export function reframeLeftWindow(widthRatio = 0.2) {
    try {
        const availWidth = window.screen.availWidth || window.screen.width;
        const availHeight = window.screen.availHeight || window.screen.height;
        const newWidth = Math.max(200, Math.floor(availWidth * widthRatio));
        const newHeight = Math.max(200, Math.floor(availHeight));
        const newLeft = 0;
        const newTop = 0;

        // Try to resize/move current window
        let moved = false;
        try {
            window.resizeTo(newWidth, newHeight);
            window.moveTo(newLeft, newTop);
            moved = true;
            return true;
        } catch (e) {
            console.warn('resize/move not allowed for current window:', e);
        }

        // If not allowed, open a new window positioned on the left as a fallback
        if (!moved) {
            const features = `width=${newWidth},height=${newHeight},left=${newLeft},top=${newTop},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no`;
            const win = window.open(window.location.href, 'leftReframe', features);
            if (!win) {
                alert('Unable to reframe the window. Your browser may be blocking popups or does not allow moving/resizing of existing windows.');
                return false;
            }
            return true;
        }
    } catch (err) {
        console.error('reframeLeftWindow error:', err);
        alert('An unexpected error occurred while trying to reframe the window. See console for details.');
        return false;
    }
}

/**
 * Opens ClassBuddy in a LEFT sidebar and reframes current window to the RIGHT
 * @param {boolean} reframeRight - Whether to reframe the current window to the right
 * @returns {Window|null} - The opened window reference or null
 */
export function openClassBuddySplitScreen(reframeRight = true) {
    const classbuddyWin = openLeftSidebar(
        'https://classbuddy.instructure.com',
        'classbuddySidebar',
        0.5
    );

    if (classbuddyWin && reframeRight) {
        // Small delay to ensure the new window opens first
        setTimeout(() => {
            reframeRightWindow(0.5);
        }, 100);
    }

    return classbuddyWin;
}

/**
 * Opens a custom URL in a LEFT sidebar and reframes current window to the RIGHT
 * @param {string} url - The URL to open
 * @param {string} name - Window name/target
 * @param {boolean} reframeRight - Whether to reframe the current window to the right
 * @returns {Window|null} - The opened window reference or null
 */
export function openCustomSplitScreen(url, name = 'customSidebar', reframeRight = true) {
    const customWin = openLeftSidebar(url, name, 0.5);

    if (customWin && reframeRight) {
        // Small delay to ensure the new window opens first
        setTimeout(() => {
            reframeRightWindow(0.5);
        }, 100);
    }

    return customWin;
}
